import { useEffect, useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label, inputStyle } from "./_roadsCommon";

/** Ch 21.4 — Corridor (alignment × profile × template) + earthwork. */
export default function CorridorViewerPanel({ projectId }) {
  const [alignments, setAlignments] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [corridors, setCorridors] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ alignment_id: "", profile_id: "",
                                       template_id: "", station_interval_m: 20 });
  const [earthwork, setEarthwork] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { (async () => {
    try {
      const [a, t, c] = await Promise.all([
        api.get(`/projects/${projectId}/roads/alignments`),
        api.get(`/projects/${projectId}/roads/cross-section-templates`),
        api.get(`/projects/${projectId}/roads/corridors`),
      ]);
      setAlignments(a.data.alignments || []);
      setTemplates(t.data.templates || []);
      setCorridors(c.data.corridors || []);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  })(); /* eslint-disable-next-line */ }, []);

  useEffect(() => { (async () => {
    if (!form.alignment_id) return;
    try {
      const { data } = await api.get(
        `/projects/${projectId}/roads/profiles?alignment_id=${form.alignment_id}`);
      setProfiles(data.profiles || []);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  })(); /* eslint-disable-next-line */ }, [form.alignment_id]);

  const buildCorridor = async () => {
    if (!form.alignment_id) return alert("Pick an alignment first.");
    try {
      const { data } = await api.post(
        `/projects/${projectId}/roads/corridors`, form);
      setActive(data.corridor);
      setCorridors([data.corridor, ...corridors]);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const computeEarthwork = async () => {
    if (!active) return;
    // Generate synthetic cut/fill areas for each station — engineer would replace
    // with real terrain-derived areas. For now: arbitrary 0–25 m² with cosine modulation.
    const stations = (active.stations || []).map((s, i) => ({
      chainage_m: s.chainage_m,
      cut_area_m2:  Math.max(0, 12 + 8 * Math.cos(i / 8)),
      fill_area_m2: Math.max(0, 8 - 8 * Math.cos(i / 8)),
    }));
    try {
      const { data } = await api.post(
        `/projects/${projectId}/roads/corridors/${active.id}/earthwork`,
        { stations });
      setEarthwork(data);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  return (
    <div data-testid="corridor-viewer" style={{ display: "grid",
            gridTemplateColumns: "320px 1fr 280px", gap: 8, height: "calc(100vh - 280px)", minHeight: 700 }}>
      <div style={{ ...card, padding: 8 }}>
        <div style={label}>Build corridor</div>
        <div style={{ marginTop: 6, fontSize: 10, color: "#888" }}>Alignment</div>
        <select data-testid="corridor-align" style={inputStyle}
          value={form.alignment_id} onChange={(e) =>
            setForm({ ...form, alignment_id: e.target.value })}>
          <option value="">— pick —</option>
          {alignments.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
        <div style={{ marginTop: 6, fontSize: 10, color: "#888" }}>Profile (optional)</div>
        <select data-testid="corridor-profile" style={inputStyle}
          value={form.profile_id} onChange={(e) =>
            setForm({ ...form, profile_id: e.target.value })}>
          <option value="">— none —</option>
          {profiles.map((p) => <option key={p.id} value={p.id}>{p.id.slice(0, 8)}</option>)}
        </select>
        <div style={{ marginTop: 6, fontSize: 10, color: "#888" }}>Template (optional)</div>
        <select data-testid="corridor-template" style={inputStyle}
          value={form.template_id} onChange={(e) =>
            setForm({ ...form, template_id: e.target.value })}>
          <option value="">— none —</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <div style={{ marginTop: 6, fontSize: 10, color: "#888" }}>Station interval (m)</div>
        <input data-testid="corridor-interval" style={inputStyle} type="number"
          value={form.station_interval_m} onChange={(e) =>
            setForm({ ...form, station_interval_m: parseFloat(e.target.value) || 20 })} />
        <button data-testid="corridor-build" style={{ ...btn, width: "100%", marginTop: 12 }}
          onClick={buildCorridor}>Build corridor</button>

        <div style={{ ...label, marginTop: 14 }}>Existing corridors</div>
        {corridors.map((c) => (
          <button key={c.id} data-testid={`corridor-row-${c.id}`}
            style={{ ...btnGhost, width: "100%", textAlign: "left", marginTop: 3,
                      background: active?.id === c.id ? "rgba(242,107,31,.20)" : "transparent" }}
            onClick={() => setActive(c)}>
            {c.id.slice(0, 8)} · {(c.total_length_m / 1000).toFixed(2)} km · {c.stations?.length || 0} stations
          </button>
        ))}
      </div>

      <div style={{ ...card, padding: 16, overflowY: "auto" }}>
        {!active ? (
          <div style={{ color: "var(--pf-muted)", fontSize: 12 }}>
            Build or select a corridor to view stations + earthwork.
          </div>
        ) : (
          <>
            <div style={{ ...label, marginBottom: 6 }}>Corridor {active.id.slice(0, 8)}</div>
            <div style={{ fontSize: 12, color: "#aaa", marginBottom: 12 }}>
              Total: <b style={{ color: "#F26B1F" }}>{(active.total_length_m / 1000).toFixed(3)} km</b> ·
              Template width: {active.template_width_m || "—"}m · Stations: {active.stations?.length || 0}
            </div>
            <button data-testid="corridor-earthwork"
              style={btn} onClick={computeEarthwork}>↗ Compute earthwork (sample)</button>
            {earthwork && (
              <div data-testid="earthwork-results" style={{ marginTop: 16, padding: 12,
                    background: "rgba(255,255,255,0.04)", borderRadius: 4 }}>
                <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
                  <div>
                    <div style={label}>Cut</div>
                    <div style={{ fontSize: 18, color: "#ef4444", fontFamily: "monospace" }}>
                      {(earthwork.cut_total_m3 / 1000).toFixed(2)} k m³</div>
                  </div>
                  <div>
                    <div style={label}>Fill</div>
                    <div style={{ fontSize: 18, color: "#3aa0ff", fontFamily: "monospace" }}>
                      {(earthwork.fill_total_m3 / 1000).toFixed(2)} k m³</div>
                  </div>
                  <div>
                    <div style={label}>Balance</div>
                    <div style={{ fontSize: 18,
                                    color: earthwork.balance_m3 > 0 ? "#3aa0ff" : "#ef4444",
                                    fontFamily: "monospace" }}>
                      {(earthwork.balance_m3 / 1000).toFixed(2)} k m³</div>
                  </div>
                </div>
                <div style={{ ...label, marginBottom: 4 }}>Mass-haul (running balance)</div>
                <svg viewBox="0 0 1000 200" style={{ width: "100%", background: "#0a1220" }}>
                  {earthwork.mass_haul.map((m, i) => i === 0 ? null : (
                    <line key={i}
                            x1={(earthwork.mass_haul[i-1].chainage_m / active.total_length_m) * 1000}
                            y1={100 - earthwork.mass_haul[i-1].ordinate_m3 / 1000}
                            x2={(m.chainage_m / active.total_length_m) * 1000}
                            y2={100 - m.ordinate_m3 / 1000}
                            stroke="#F26B1F" strokeWidth={1.5} />
                  ))}
                  <line x1={0} y1={100} x2={1000} y2={100} stroke="#444" strokeDasharray="4,4" />
                </svg>
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ ...card, padding: 8, overflowY: "auto" }}>
        <div style={label}>Help</div>
        <p style={{ fontSize: 10, color: "#aaa", lineHeight: 1.6 }}>
          A <b>corridor</b> combines a horizontal alignment, vertical profile, and cross-section template
          to produce the 3-D road model. Once built, station-by-station cut/fill areas are integrated by
          the prismoidal formula to give cumulative earthwork volumes and a mass-haul ordinate at each station.
        </p>
        {err && <div style={{ marginTop: 8, color: "#FF8888", fontSize: 11 }}>{err}</div>}
      </div>
    </div>
  );
}
